import { useEffect, useCallback } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { useCarForm } from '../hooks/useCarForm';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { ProgressHeader } from '../components/ProgressHeader';
import { StepBasicInfo } from '../components/StepBasicInfo';
import { StepTechnicalInfo } from '../components/StepTechnicalInfo';
import { StepContactInfo } from '../components/StepContactInfo';

const TOTAL_STEPS = 3;

export function CarForm() {
  const { tg, startParam } = useTelegram();
  const { form, loading, submitting, submitForm } = useCarForm(startParam);
  const { step, goNext, goBack, isFirstStep, isLastStep, progress, totalSteps } =
    useStepNavigation(TOTAL_STEPS);

  const { handleSubmit, formState: { isValid } } = form;

  const onSubmit = useCallback(async () => {
    await handleSubmit(
      async (data) => {
        const result = await submitForm(data);
        if (result.success) {
          // API sends Telegram confirmation message, just close mini-app
          if (tg) {
            tg.close();
          } else {
            alert("Ma'lumotlar saqlandi!");
          }
        } else {
          alert('Xatolik: ' + (result.error || "Ma'lumotlarni saqlashda xatolik"));
        }
      },
      (errors) => {
        const firstError = Object.values(errors)[0];
        alert((firstError?.message || "Formada xatolik bor") as string);
      },
    )();
  }, [handleSubmit, submitForm, tg]);

  // Telegram MainButton
  useEffect(() => {
    if (!tg?.MainButton) return;

    const mainButton = tg.MainButton;

    if (!isLastStep) {
      mainButton.setText('Keyingisi');
      mainButton.show();
      mainButton.enable();
      mainButton.onClick(goNext);
      return () => {
        mainButton.offClick(goNext);
      };
    }

    mainButton.setText(submitting ? 'Yuklanmoqda...' : 'Yuborish');
    mainButton.show();

    if (isValid && !submitting) {
      mainButton.enable();
      mainButton.onClick(onSubmit);
    } else {
      mainButton.disable();
    }

    return () => {
      mainButton.offClick(onSubmit);
    };
  }, [tg, isLastStep, isValid, submitting, goNext, onSubmit]);

  // Telegram BackButton
  useEffect(() => {
    if (!tg?.BackButton) return;

    if (!isFirstStep) {
      tg.BackButton.show();
      tg.BackButton.onClick(goBack);
    } else {
      tg.BackButton.hide();
    }

    return () => {
      tg.BackButton.offClick(goBack);
    };
  }, [tg, isFirstStep, goBack]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <ProgressHeader step={step} totalSteps={totalSteps} progress={progress} />

      <form onSubmit={(e) => e.preventDefault()} className="form">
        {step === 1 && <StepBasicInfo form={form} />}
        {step === 2 && <StepTechnicalInfo form={form} />}
        {step === 3 && <StepContactInfo form={form} />}
      </form>

      {/* Fallback buttons for non-Telegram environment */}
      {!tg?.MainButton && (
        <div className="nav-buttons">
          {!isFirstStep && (
            <button type="button" className="back-btn" onClick={goBack}>
              Orqaga
            </button>
          )}
          {!isLastStep ? (
            <button type="button" className="next-btn" onClick={goNext}>
              Keyingisi
            </button>
          ) : (
            <button
              type="button"
              className="submit-btn"
              disabled={!isValid || submitting}
              onClick={onSubmit}
            >
              {submitting ? 'Yuklanmoqda...' : 'Yuborish'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
